/**
 * El SSOT de diseño de la app móvil, traducido a CSS para la previsualización web.
 *
 * ## Por qué se GENERA y no se escribe a mano
 *
 * Los valores viven en `@idc-forms/client-app-domain/theme/tokens` y son los mismos que consume
 * la app. Copiarlos a mano acá garantizaría que el día que diseño mueva un gris, la
 * previsualización siga mostrando el viejo — y una previsualización que miente es peor que no
 * tenerla. Así que el `.css` es un ARTEFACTO: lo produce `buildCapTokensCss` y su spec falla si
 * el archivo commiteado dejó de coincidir con los tokens.
 *
 * ## Por qué las clases son propias y no utilidades de Tailwind
 *
 * ☠️ Medido en el port de Figma de la app: `tailwind-merge` no puede decidir si `text-[var(--x)]`
 * es un TAMAÑO o un COLOR, y colapsa la clase equivocada — un `h1` terminó saliendo más chico que
 * un `h3`. Con nombres propios (`cap-text-h1`, `cap-fg-brand-700`) `twMerge` no los conoce, no los
 * agrupa y no los pisa: pasan tal cual. La maquetación (flex, gap, padding, tamaños) sí va en
 * Tailwind, donde `twMerge` sabe lo que hace.
 *
 * Tailwind 4 de la web NO se toca (E10 del contexto común): esto es una hoja aparte que se importa
 * desde el componente raíz del preview y sólo aplica bajo `.client-app-preview`.
 */

/** La clase del nodo raíz del marco. Todas las variables `--cap-` cuelgan de acá. */
export const CAP_ROOT_CLASS = 'client-app-preview'

/** La forma de `tokens.js` que este generador necesita. Estructural: no importa el módulo. */
export type CapTokenSource = {
  colors: Record<string, Record<string, string>>
  fontSize: Record<string, [string, { lineHeight: string }]>
  spacing: Record<string, string>
  borderRadius: Record<string, string>
}

/**
 * Los pesos de la rampa de Roboto, por la llave con la que `tokens.fontFamily` los nombra.
 *
 * En la app cada peso es una FAMILIA distinta (`Roboto-Medium`), porque React Native no compone
 * familia + peso. En la web sí: una sola familia con siete `@font-face` y el peso numérico. Por eso
 * este mapa vive acá y no en los tokens — es la traducción de un detalle de plataforma, no un valor
 * de diseño.
 */
const FONT_WEIGHTS = {
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700
} as const

export type CapFontWeight = keyof typeof FONT_WEIGHTS

/** `surface.headerTint` → `surface-header-tint`. Las variables CSS van en kebab. */
function kebab(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
}

/** Aplana `colors` a `[nombre, valor]`: `brand.700` → `brand-700`, `surface.cover` → `surface-cover`. */
export function flattenColors(colors: CapTokenSource['colors']): Array<[string, string]> {
  const entries: Array<[string, string]> = []

  for (const [group, scale] of Object.entries(colors)) {
    for (const [step, value] of Object.entries(scale)) {
      entries.push([`${kebab(group)}-${kebab(step)}`, value])
    }
  }

  return entries
}

/**
 * Los hex van en minúscula porque **Biome los baja al formatear el `.css`**. Si el generador los
 * emitiera como los escribe `tokens.js` —en versales— el archivo saldría distinto de lo generado
 * apenas alguien corriera `pnpm check:fix`, y el spec fallaría por una diferencia de caja que no
 * le cambia el color a nadie. Se normaliza acá, una vez, en vez de discutirlo cada vez.
 */
function cssValue(value: string): string {
  if (value.startsWith('#')) return value.toLowerCase()
  // Mismo motivo con los `rgba(0,0,0,0.5)` de `surface`: Biome les mete el espacio tras la coma.
  return value.replace(/,\s*/g, ', ')
}

function variables(source: CapTokenSource): string[] {
  const lines: string[] = []

  for (const [name, value] of flattenColors(source.colors)) {
    lines.push(`  --cap-${name}: ${cssValue(value)};`)
  }

  // Las dos familias que la app carga desde `assets/fonts/`. El fallback no es decorativo: hasta
  // que el `.ttf` termina de bajar el navegador dibuja con él, y una sans genérica mantiene la
  // métrica mucho más cerca que la serif por defecto.
  lines.push(`  --cap-font-sans: "Roboto CAP", system-ui, -apple-system, sans-serif;`)
  lines.push(`  --cap-font-display: "Arsenal SC CAP", Georgia, serif;`)

  for (const [name, [size, { lineHeight }]] of Object.entries(source.fontSize)) {
    lines.push(`  --cap-text-${kebab(name)}: ${size};`)
    lines.push(`  --cap-leading-${kebab(name)}: ${lineHeight};`)
  }

  for (const [name, value] of Object.entries(source.spacing)) {
    lines.push(`  --cap-space-${kebab(name)}: ${value};`)
  }

  for (const [name, value] of Object.entries(source.borderRadius)) {
    lines.push(`  --cap-radius-${kebab(name)}: ${value};`)
  }

  return lines
}

/** Una regla, en la forma en la que Biome formatea CSS: una declaración por renglón. */
function rule(selector: string, declarations: string[]): string {
  return [`${selector} {`, ...declarations.map((line) => `  ${line}`), '}'].join('\n')
}

function utilities(source: CapTokenSource): string[] {
  const lines: string[] = []

  for (const [weight, numeric] of Object.entries(FONT_WEIGHTS)) {
    lines.push(
      rule(`.cap-font-${kebab(weight)}`, [
        'font-family: var(--cap-font-sans);',
        `font-weight: ${numeric};`
      ])
    )
  }
  lines.push(
    rule('.cap-font-display', ['font-family: var(--cap-font-display);', 'font-weight: 400;'])
  )
  lines.push(
    rule('.cap-font-display-bold', ['font-family: var(--cap-font-display);', 'font-weight: 700;'])
  )

  for (const name of Object.keys(source.fontSize)) {
    const key = kebab(name)
    lines.push(
      rule(`.cap-text-${key}`, [
        `font-size: var(--cap-text-${key});`,
        `line-height: var(--cap-leading-${key});`
      ])
    )
  }

  for (const [name] of flattenColors(source.colors)) {
    lines.push(rule(`.cap-fg-${name}`, [`color: var(--cap-${name});`]))
    lines.push(rule(`.cap-bg-${name}`, [`background-color: var(--cap-${name});`]))
    lines.push(rule(`.cap-border-${name}`, [`border-color: var(--cap-${name});`]))
  }

  return lines
}

/** El aviso que encabeza el `.css`. Se compara en el spec, así que es parte del contrato. */
export const CAP_TOKENS_BANNER = [
  '/*',
  ' * GENERADO por `cap-tokens.ts` desde `@idc-forms/client-app-domain/theme/tokens`.',
  ' * No lo edites a mano: `cap-tokens.spec.ts` lo vuelve a generar y compara.',
  ' */'
].join('\n')

export function buildCapTokensCss(source: CapTokenSource): string {
  return [
    CAP_TOKENS_BANNER,
    '',
    `.${CAP_ROOT_CLASS} {`,
    ...variables(source),
    '}',
    '',
    ...utilities(source),
    ''
  ].join('\n')
}
